<script setup>
import { ref, computed } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/ui/popover";
import StatusDot from "@/shared/ui/status-dot/StatusDot.vue";
import { getStatusLabel } from "@/shared/config/statusDictionary";
import { useAvailableActions } from "@/features/notarial-actions/composables/useAvailableActions";
import { createNotaryActionFromApplication } from "@/entities/application";
import { createStepRun } from "@/entities/transaction";
import { getTemplateItem } from "@/entities/template";
import { toast } from "@/shared/store/alertStore";
import NotaryApplicationPicker from "./NotaryApplicationPicker.vue";

const props = defineProps({
  transactionId: {
    type: [String, Number],
    required: true,
  },
  currentStatus: { type: String, default: "" },
});

const emit = defineEmits(["applied"]);

const { t } = useI18n();
const router = useRouter();

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isUuid(value) {
  return typeof value === "string" && UUID_RE.test(value);
}

const { availableActions, refresh } = useAvailableActions(() => {
  const id = props.transactionId;
  if (isUuid(id)) return { applicationUuid: id };
  return { transactionId: id };
});

const submitting = ref(false);
const popoverOpen = ref(false);

const pickerOpen = ref(false);
const pickerActions = ref([]);
const pendingTargetStatus = ref("");

const hasActions = computed(() => availableActions.value.length > 0);

function onChooseAction(action) {
  if (!action || submitting.value) return;
  popoverOpen.value = false;

  const codes = action.raw?.notary_action_codes;
  if (Array.isArray(codes) && codes.length) {
    pickerActions.value = codes;
    pendingTargetStatus.value = action.code;
    pickerOpen.value = true;
    return;
  }

  submit(action.code, null);
}

function onPickNotaryAction(item) {
  if (!item || !pendingTargetStatus.value) return;
  submit(pendingTargetStatus.value, item.code);
}

function onPickerClose() {
  pickerOpen.value = false;
  pickerActions.value = [];
  pendingTargetStatus.value = "";
}

function extractNewApplication(res) {
  const p = res?.payload ?? res ?? {};
  const candidates = [p?.new_tx, p?.new_transaction, p?.application, p];
  for (const c of candidates) {
    if (c && (c.id || c.uuid)) {
      return { id: c.id ?? null, uuid: c.uuid ?? null };
    }
  }
  return null;
}

async function initStepRun(application, notary_action_code) {
  try {
    const tplRes = await getTemplateItem(notary_action_code);
    const tpl = tplRes?.payload || null;
    const response = await createStepRun({
      notary_transaction_id: application.id,
      notary_transaction_uuid: application.uuid,
      template_uuid: tpl?.uuid,
    });
    if (!response?.success) {
      console.warn("[StatusActionPicker] createStepRun failed", response);
    }
  } catch (e) {
    console.warn("[StatusActionPicker] initStepRun error", e);
  }
}

async function submit(target_status, notary_action_code) {
  if (!props.transactionId) return;
  submitting.value = true;
  try {
    const payload = { target_status };
    if (notary_action_code) payload.notary_action_code = notary_action_code;

    const res = await createNotaryActionFromApplication(
      props.transactionId,
      payload
    );
    if (res?.success === false) {
      const msg =
        typeof res.error === "string"
          ? res.error
          : res.error?.detail || t("Не удалось изменить статус");
      toast.error(msg);
      return;
    }

    if (notary_action_code) {
      const application = extractNewApplication(res);
      if (application && (application.id || application.uuid)) {
        await initStepRun(application, notary_action_code);
        toast.success(t("Заявление создано"));
        router.push({
          name: "client_application_edit",
          params: { id: String(application.id ?? application.uuid) },
        });
        return;
      }
    }

    toast.success(t("Статус обновлён"));
    await refresh();
    emit("applied");
  } finally {
    submitting.value = false;
  }
}
</script>

<template>
  <div class="flex items-center gap-2">
    <Popover v-if="hasActions" v-model:open="popoverOpen">
      <PopoverTrigger as-child>
        <button
          type="button"
          :disabled="submitting"
          class="inline-flex items-center gap-2 text-sm text-foreground border border-border rounded-md px-3 h-9 bg-card hover:bg-muted disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
        >
          <StatusDot v-if="currentStatus" :code="currentStatus" />
          <span>
            {{
              currentStatus
                ? getStatusLabel(currentStatus)
                : $t("Изменить статус")
            }}
          </span>
          <svg-icon name="huge-down" class="w-4 h-4 text-muted-foreground" />
        </button>
      </PopoverTrigger>

      <PopoverContent align="end" class="w-64 p-1">
        <div class="text-xs text-muted-foreground px-2 py-1.5">
          {{ $t("Изменить статус") }}
        </div>
        <button
          v-for="action in availableActions"
          :key="action.code"
          type="button"
          :disabled="submitting"
          class="w-full flex items-center gap-2 text-left px-2 py-2 rounded-sm text-sm hover:bg-muted disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
          @click="onChooseAction(action)"
        >
          <span
            class="inline-block w-2 h-2 rounded-full flex-shrink-0"
            :style="{ backgroundColor: action.raw?.color || '#94A3B8' }"
          />
          <span class="flex-1">{{ action.label }}</span>
        </button>
      </PopoverContent>
    </Popover>

    <div
      v-else-if="currentStatus"
      class="inline-flex items-center gap-2 text-sm text-foreground border border-border rounded-md px-3 h-9 bg-card"
    >
      <StatusDot :code="currentStatus" />
      {{ getStatusLabel(currentStatus) }}
    </div>

    <NotaryApplicationPicker
      v-if="pickerOpen"
      :title="$t('Выберите нотариальное действие')"
      :actions="pickerActions"
      @select="onPickNotaryAction"
      @close="onPickerClose"
    />
  </div>
</template>
